import { Children, useCallback, useRef } from 'react';

import {
  AccordionProps as Props,
  ARROW_DOWN,
  ARROW_UP,
  HOME,
  END,
  ENTER,
} from './accordion.constants';
import { AccordionContext, SelectableContext } from './accordion.context';
import AccordionToggle from './accordion-toggle';

const Accordion = ({
  id,
  children,
  allyTabToButton,
  onSelect,
  onAllExpanded,
  onAllCollapsed,
}: Props) => {
  const ref = useRef<HTMLDivElement | null>(null);

  const getAccordionTriggers = useCallback(() => {
    if (ref && ref.current) {
      const nodes = ref.current.querySelectorAll(
        allyTabToButton
          ? '.accordion-trigger-button'
          : `[data-accordion-id="${id}"]`
      ) as NodeListOf<HTMLElement>;
      return [].slice.call(nodes);
    }
    return [];
  }, [id, allyTabToButton]);

  const onAccordionToggle = useCallback(
    (isExpand: boolean) => {
      let count = 0;
      const triggers = getAccordionTriggers();
      triggers.forEach((t: HTMLElement) => {
        if (t.getAttribute('aria-expanded') === isExpand.toString()) {
          count++;
        }
      });
      if (count + 1 === triggers.length) {
        if (isExpand && onAllExpanded) {
          onAllExpanded();
        } else if (onAllCollapsed) {
          onAllCollapsed();
        }
      }
    },
    [getAccordionTriggers, onAllExpanded, onAllCollapsed]
  );

  const focusFirst = useCallback((accordionTriggers: HTMLElement[]) => {
    accordionTriggers[0].focus();
  }, []);

  const focusLast = useCallback((accordionTriggers: HTMLElement[]) => {
    accordionTriggers[accordionTriggers.length - 1].focus();
  }, []);

  const focusNext = useCallback(
    (currentIndex: number, accordionTriggers: HTMLElement[]) => {
      if (currentIndex === accordionTriggers.length - 1) {
        focusFirst(accordionTriggers);
        return;
      }
      accordionTriggers[currentIndex + 1].focus();
    },
    [focusFirst]
  );

  const focusPrevious = useCallback(
    (currentIndex: number, accordionTriggers: HTMLElement[]) => {
      if (currentIndex === 0) {
        focusLast(accordionTriggers);
        return;
      }
      accordionTriggers[currentIndex - 1].focus();
    },
    [focusLast]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const accordionTriggers = getAccordionTriggers() as HTMLElement[];
      const { key } = event;
      const target = event.target as HTMLElement;

      switch (key) {
        case ARROW_DOWN: {
          event.preventDefault();
          const currentIndex = accordionTriggers.indexOf(target);
          if (currentIndex >= 0) focusNext(currentIndex, accordionTriggers);
          break;
        }
        case ARROW_UP: {
          event.preventDefault();
          const currentIndex = accordionTriggers.indexOf(target);
          if (currentIndex >= 0) focusPrevious(currentIndex, accordionTriggers);
          break;
        }
        case HOME: {
          event.preventDefault();
          focusFirst(accordionTriggers);
          break;
        }
        case END: {
          event.preventDefault();
          focusLast(accordionTriggers);
          break;
        }
        case ENTER: {
          // could use target.matches but for IE11
          if (target.classList.contains('accordion-trigger')) {
            event.preventDefault();
            const currentIndex = accordionTriggers.indexOf(target);
            accordionTriggers[currentIndex].click();
          }
          break;
        }
      }
    },
    [focusNext, focusPrevious, focusFirst, focusLast, getAccordionTriggers]
  );

  return (
    <AccordionContext.Provider
      value={{
        onHeaderKeyDown: handleKeyDown,
        onToggle: onAccordionToggle,
        id: id,
      }}
    >
      <div data-selector="accordion" className="accordion" ref={ref}>
        {Children.toArray(children).map((child, idx) => (
          <SelectableContext.Provider
            key={`ctx.${idx}`}
            value={{
              onSelect: onSelect,
              index: idx,
            }}
          >
            {child}
          </SelectableContext.Provider>
        ))}
      </div>
    </AccordionContext.Provider>
  );
};

export { AccordionToggle };

export default Accordion;
